/**
 * งานยามออฟไลน์ and ลักพาตัว (docs/systems/OFFLINE_WORK.md): the jobs a character works at while their player is
 * away, the spots on each map where the bodies stand at them, the offline bodies of other players this
 * single-player build stands there in their stead (as the arena's offline fighters, data/arena.ts), the
 * places no one may be kidnapped, and the black-market buyers who pay for a sack.
 *
 * Data only; the rules are `offlineWork.ts` (time away) and `systems/offlineWork.ts` (the bodies, the
 * blow, the sack). A zone without entries here has no bodies, buyers or spots: nothing changes there.
 */
import { PK_MIN_RANK } from '../karma';
import type { ProfessionId } from './professions';
import type { ZoneId } from './zones';
import { MAP_BODIES, MAP_BUYERS, MAP_NO_KIDNAP, MAP_SPOTS } from './offlineWorkMaps';

export type OfflineJobId =
  | 'porter'
  | 'sweeper'
  | 'musician'
  | 'woodcut'
  | 'mine'
  | 'fish'
  | 'herb'
  | 'farm'
  | 'smith'
  | 'tailor'
  | 'cook';

export interface OfflineJobDef {
  readonly id: OfflineJobId;
  /** Thai, for the picker and the report. */
  readonly name: string;
  /** The trade it uses (its level raises the pay), or null for plain work anyone does. */
  readonly prof: ProfessionId | null;
  /** The act the body plays at its spot (data/npcActs.ts `NPC_ACTS` id; the renderer plays its motion). */
  readonly act: string;
  /** Silver (ตำลึง) an hour at trade level 1 (plain work: always). */
  readonly silver: number;
  readonly desc: string;
  /** Goods an hour at trade level 1 when paid in goods instead of silver (data/professions.ts GOODS ids). */
  readonly goods?: readonly { readonly id: string; readonly per: number }[];
}

const jobs: OfflineJobDef[] = [
  { id: 'porter', name: 'หาบของรับจ้าง', prof: null, act: 'carryBuckets', silver: 40, desc: 'หาบน้ำหาบของให้ร้านรอบลาน ใครก็ทำได้' },
  { id: 'sweeper', name: 'กวาดลานรับจ้าง', prof: null, act: 'sweep', silver: 36, desc: 'กวาดลานหน้าศาลาและร้านค้า ค่าแรงน้อยแต่ปลอดภัยกว่า' },
  { id: 'musician', name: 'บรรเลงพิณเปิดหมวก', prof: null, act: 'guqin', silver: 44, desc: 'ดีดกู่ฉินริมศาลา คนผ่านไปมาโยนเหรียญให้' },
  { id: 'woodcut', name: 'ผ่าฟืนขาย', prof: 'woodcutter', act: 'chopWood', silver: 50, desc: 'ผ่าฟืนข้างเพิง ยิ่งขั้นคนตัดไม้สูงยิ่งได้มาก', goods: [{ id: 'pine-log', per: 5 }, { id: 'resin', per: 2 }] },
  { id: 'mine', name: 'ทุบหินแยกแร่', prof: 'miner', act: 'hammer', silver: 52, desc: 'ทุบหินแยกแร่ให้ช่างตีเหล็ก' },
  { id: 'fish', name: 'หาบปลาส่งร้าน', prof: 'fisher', act: 'carryBuckets', silver: 48, desc: 'หาบปลาจากท่าน้ำส่งร้านอาหาร', goods: [{ id: 'carp', per: 6 }] },
  { id: 'herb', name: 'ต้มยาขาย', prof: 'herbalist', act: 'pourTea', silver: 54, desc: 'ต้มยาสมุนไพรขายคนเดินทาง' },
  { id: 'farm', name: 'รับจ้างดำนา', prof: 'farmer', act: 'plantRice', silver: 46, desc: 'ดำนาและถอนหญ้าในแปลงผัก', goods: [{ id: 'rice', per: 8 }, { id: 'herb-leaf', per: 2 }] },
  { id: 'smith', name: 'ตีเหล็กรับจ้าง', prof: 'blacksmith', act: 'hammer', silver: 58, desc: 'ตีเกือกม้า ตะปู และใบมีดให้ร้าน' },
  { id: 'tailor', name: 'ซักย้อมผ้า', prof: 'tailor', act: 'washClothes', silver: 52, desc: 'ซักและย้อมผ้าให้ร้านเสื้อ' },
  { id: 'cook', name: 'ร้องขายอาหาร', prof: 'cook', act: 'hawk', silver: 54, desc: 'ร้องขายซาลาเปาและบะหมี่หน้าแผง' },
];
export const OFFLINE_JOB_LIST: readonly OfflineJobDef[] = jobs;
export const OFFLINE_JOBS: Readonly<Record<OfflineJobId, OfflineJobDef>> = Object.fromEntries(
  jobs.map((j) => [j.id, j]),
) as Record<OfflineJobId, OfflineJobDef>;
export const isOfflineJobId = (v: unknown): v is OfflineJobId => typeof v === 'string' && v in OFFLINE_JOBS;

/** The rules' numbers. */
export const OFFLINE_WORK = {
  /** Pay rises this much per trade level above 1 (level 10: ×2.35). */
  perLevel: 0.15,
  /** Away for less than this (seconds): no report, and the character is not moved to the work spot. */
  reportMin: 300,
  /**
   * The chance of being kidnapped while away: 1 − e^(−hours × rate × risk), `risk` the spot's (0–1).
   * Four hours at the riskiest spot: about 27 %.
   */
  kidnapRate: 0.08,
  /** A spot's risk where the zone has no spot for the job. */
  defaultRisk: 0.5,
  /** Trade xp an hour of a trade job (capped as the silver is: 4 h at most). */
  xpPerHour: 6,
} as const;

/** ชื่อฉาว (sacks sold): what it costs, and how it fades. */
export const INFAMY = {
  /** One point fades per this many seconds of play (in the World, not while away). */
  decaySeconds: 2 * 3600,
  /** From here the name tag reads red (`Entity.infamous`) and the merchant asks more. */
  red: 3,
  priceMult: 1.2,
  /** From here the town guards will not talk. */
  guards: 6,
} as const;

export const KIDNAP = {
  /** How near the blower must stand (metres). */
  blowRange: 2,
  /** The blow's wind-up (seconds): moving or being struck in it breaks it off. */
  blowTime: 1.5,
  /** How far the blower may drift during the blow before it counts as moving (metres). */
  blowDrift: 0.25,
  /** How near a sack must be to pick it up, and a buyer to sell to (metres). */
  pickRange: 2,
  sellRange: 3,
  /** The carrier's pace (the `sack-carry` status, status.ts: no sprint, qinggong or mount either). */
  carrySpeed: 0.6,
  /** An unsold sack wakes after this long (seconds). */
  wakeAfter: 300,
  /** After a kidnapping ends (sold, woke, freed), the victim cannot be taken again for this long (seconds). */
  cooldown: 600,
  /** Victims below this power rank (cultivation.ts `rankFor`; 9 = ฝึกลมปราณ ชั้น 1) are protected. */
  rankFloor: PK_MIN_RANK,
  /** Price: (base + perRank × the victim's rank) × (1 + metres from the kidnap spot / perMetres), at most ×maxMult. */
  priceBase: 60,
  priceRank: 8,
  pricePerMetres: 40,
  priceMaxMult: 4,
  /** ชื่อฉาว gained per sack sold. */
  infamy: 1,
  /** Silver for freeing a carried sack (base + per victim rank). */
  rescueBase: 40,
  rescueRank: 4,
} as const;

/** A sack's price at a buyer: by the victim's power rank and how far it was carried from where it was taken. */
export function sackPrice(rank: number, metres: number): number {
  const mult = Math.min(KIDNAP.priceMaxMult, 1 + Math.max(0, metres) / KIDNAP.pricePerMetres);
  return Math.round((KIDNAP.priceBase + KIDNAP.priceRank * Math.max(0, rank)) * mult);
}

/**
 * A sack sale's money (ECONOMY E6): this share of the price comes out of the victim's away earnings (they see it in
 * the report); the rest is new money. Never more than the victim earned.
 */
export const SACK_VICTIM_SHARE = 0.5;
export function sackVictimLoss(price: number, earned: number): number {
  return Math.max(0, Math.min(Math.max(0, Math.floor(earned)), Math.round(price * SACK_VICTIM_SHARE)));
}

/** Silver for freeing a sack of the given victim rank. */
export const rescueReward = (rank: number): number => KIDNAP.rescueBase + KIDNAP.rescueRank * Math.max(0, rank);

/** Where a job is worked on a map: the body stands at `pos`; `risk` (0–1) is how exposed the spot is. */
export interface OfflineSpot {
  readonly zone: ZoneId;
  readonly job: OfflineJobId;
  readonly pos: readonly [number, number];
  readonly risk: number;
}

/** A black-market buyer (พ่อค้าตลาดมืด): buys sacks, sells knockout powder. */
export interface SackBuyer {
  /** Unique across zones (the save names the buyer a character woke beside). */
  readonly id: string;
  readonly zone: ZoneId;
  readonly name: string;
  readonly title: string;
  readonly sprite: string;
  /** Where the place is, for the report ("ขายให้ … ที่ …"). */
  readonly place: string;
  readonly pos: readonly [number, number];
}

/** Another player's offline body, simulated in this single-player build (data/arena.ts does the same for the ring). */
export interface OfflineBody {
  readonly zone: ZoneId;
  readonly name: string;
  readonly sprite: string;
  readonly job: OfflineJobId;
  /** Power rank (cultivation.ts `rankFor` index). */
  readonly rank: number;
}

/** A circle where no one may be kidnapped (metres). */
export interface NoKidnapArea {
  readonly zone: ZoneId;
  readonly at: readonly [number, number];
  readonly r: number;
}

/**
 * The places of each zone where kidnapping is barred. A zone listed here uses these circles in place of its
 * PK-safe outline (the demo plaza is PK-safe wall to wall, but only its middle is guarded); a zone not listed
 * uses its safe zones (World `inSafeZone`: data/safeZones.ts where the zone has them, as the new Yanyu does, else its
 * PK-safe outline). School compounds and the inside of buildings are always barred.
 */
export const NO_KIDNAP: readonly NoKidnapArea[] = [
  // The demo plaza's core round the spawn, the elder and the merchant.
  { zone: 'demoPlaza', at: [40, 40], r: 12 },
  ...MAP_NO_KIDNAP,
];

export const OFFLINE_SPOTS: readonly OfflineSpot[] = [
  { zone: 'demoPlaza', job: 'porter', pos: [24, 46], risk: 0.5 },
  { zone: 'demoPlaza', job: 'sweeper', pos: [55, 34], risk: 0.3 },
  { zone: 'demoPlaza', job: 'musician', pos: [58, 25], risk: 0.4 },
  { zone: 'demoPlaza', job: 'woodcut', pos: [64, 58], risk: 0.9 },
  { zone: 'demoPlaza', job: 'mine', pos: [62, 14], risk: 0.8 },
  { zone: 'demoPlaza', job: 'fish', pos: [64, 38], risk: 0.6 },
  { zone: 'demoPlaza', job: 'herb', pos: [17, 41], risk: 0.6 },
  { zone: 'demoPlaza', job: 'farm', pos: [16, 61], risk: 1 },
  { zone: 'demoPlaza', job: 'smith', pos: [46, 18], risk: 0.4 },
  { zone: 'demoPlaza', job: 'tailor', pos: [22, 22], risk: 0.5 },
  { zone: 'demoPlaza', job: 'cook', pos: [52.5, 62], risk: 0.7 },
  // The new Yanyu (data/layouts/yanyu2.ts): out of its safe zones (data/safeZones.ts), on both banks.
  { zone: 'yanyu2', job: 'farm', pos: [80, 350], risk: 1 },
  { zone: 'yanyu2', job: 'woodcut', pos: [145, 440], risk: 0.9 },
  { zone: 'yanyu2', job: 'mine', pos: [534.5, 224.5], risk: 0.8 },
  { zone: 'yanyu2', job: 'fish', pos: [513.5, 519.5], risk: 0.6 },
  { zone: 'yanyu2', job: 'porter', pos: [300, 480], risk: 0.6 },
  { zone: 'yanyu2', job: 'herb', pos: [200, 302], risk: 0.5 },
  { zone: 'yanyu2', job: 'smith', pos: [454, 380], risk: 0.4 },
  { zone: 'yanyu2', job: 'tailor', pos: [354.5, 396.5], risk: 0.4 },
  { zone: 'yanyu2', job: 'cook', pos: [454, 260], risk: 0.4 },
  { zone: 'yanyu2', job: 'musician', pos: [350, 250], risk: 0.3 },
  { zone: 'yanyu2', job: 'sweeper', pos: [300, 245], risk: 0.2 },
  ...MAP_SPOTS,
];

export const SACK_BUYERS: readonly SackBuyer[] = [
  { id: 'demo-sw', zone: 'demoPlaza', name: 'เฒ่าหน้าบาก', title: 'พ่อค้าตลาดมืด', sprite: 'bandit', place: 'หลังศาลเจ้า', pos: [8, 70] },
  { id: 'demo-ne', zone: 'demoPlaza', name: 'อาโหวมือดำ', title: 'พ่อค้าตลาดมืด', sprite: 'night', place: 'ใต้หอสังเกตการณ์', pos: [75, 18] },
  { id: 'demo-nw', zone: 'demoPlaza', name: 'นางงูเขียว', title: 'พ่อค้าตลาดมืด', sprite: 'lilac', place: 'หลังโรงเตี๊ยม', pos: [24, 8] },
  // The new Yanyu: two on each bank, in its back corners.
  { id: 'y2-west-homes', zone: 'yanyu2', name: 'เฒ่าหน้าบาก', title: 'พ่อค้าตลาดมืด', sprite: 'bandit', place: 'ซอยหลังย่านบ้านตะวันตก', pos: [161.5, 259.5] },
  { id: 'y2-bandit-road', zone: 'yanyu2', name: 'อาโหวมือดำ', title: 'พ่อค้าตลาดมืด', sprite: 'night', place: 'ทางขึ้นเนินโจร', pos: [240, 490] },
  { id: 'y2-north-bridge', zone: 'yanyu2', name: 'นางงูเขียว', title: 'พ่อค้าตลาดมืด', sprite: 'lilac', place: 'ปลายสะพานเหนือฝั่งตะวันออก', pos: [445, 130] },
  { id: 'y2-east-homes', zone: 'yanyu2', name: 'หลิวตาเดียว', title: 'พ่อค้าตลาดมืด', sprite: 'bandit', place: 'ท้ายย่านบ้านฝั่งตะวันออก', pos: [539.5, 413.5] },
  ...MAP_BUYERS,
];

export const OFFLINE_BODIES: readonly OfflineBody[] = [
  { zone: 'demoPlaza', name: 'ใบไผ่ร่วง', sprite: 'taoist', job: 'woodcut', rank: 14 },
  { zone: 'demoPlaza', name: 'หมาป่าเดียวดาย', sprite: 'red', job: 'farm', rank: 22 },
  { zone: 'demoPlaza', name: 'แม่นางดอกท้อ', sprite: 'blissLady', job: 'musician', rank: 11 },
  { zone: 'demoPlaza', name: 'ภูผาเหล็ก', sprite: 'monk', job: 'smith', rank: 30 },
  { zone: 'demoPlaza', name: 'บัณฑิตฝนพรำ', sprite: 'scholar', job: 'herb', rank: 17 },
  // A newcomer below the floor: protected (the prompt says so).
  { zone: 'demoPlaza', name: 'ศิษย์ใหม่หลี่', sprite: 'guardOfficer', job: 'porter', rank: 3 },
  { zone: 'yanyu2', name: 'ใบไผ่ร่วง', sprite: 'taoist', job: 'woodcut', rank: 14 },
  { zone: 'yanyu2', name: 'หมาป่าเดียวดาย', sprite: 'red', job: 'farm', rank: 22 },
  { zone: 'yanyu2', name: 'แม่นางดอกท้อ', sprite: 'blissLady', job: 'musician', rank: 11 },
  { zone: 'yanyu2', name: 'ภูผาเหล็ก', sprite: 'monk', job: 'smith', rank: 30 },
  { zone: 'yanyu2', name: 'บัณฑิตฝนพรำ', sprite: 'scholar', job: 'herb', rank: 17 },
  { zone: 'yanyu2', name: 'คลื่นทะเล', sprite: 'night', job: 'fish', rank: 19 },
  { zone: 'yanyu2', name: 'หินผา', sprite: 'heroWudang', job: 'mine', rank: 25 },
  { zone: 'yanyu2', name: 'ศิษย์ใหม่จาง', sprite: 'guardOfficer', job: 'porter', rank: 4 },
  ...MAP_BODIES,
];

export const spotsFor = (zone: string, job?: OfflineJobId): OfflineSpot[] =>
  OFFLINE_SPOTS.filter((s) => s.zone === zone && (!job || s.job === job));
export const buyersIn = (zone: string): SackBuyer[] => SACK_BUYERS.filter((b) => b.zone === zone);
